import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Card,
  Table,
  Input,
  Select,
  Space,
  Button,
  Typography,
  message,
  Tag,
  Tooltip,
  Popconfirm,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  CopyOutlined,
  DownloadOutlined,
  EyeOutlined,
  DeleteOutlined,
  FileOutlined,
  FilePdfOutlined,
  FileImageOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import DocumentUpload from '@/components/documents/DocumentUpload';
import { useDocumentsPaged, useDeleteDocument } from '@/hooks/useDocuments';
import { documentService, type Document } from '@/services/document.service';
import { useAuthStore } from '@/store/authStore';
import { API_BASE_URL } from '@/config/apiBaseUrl';

const { Text, Title } = Typography;

const ENTITY_FILTER_OPTIONS = [
  { value: '', label: 'All entities' },
  { value: 'GrnItemHeatLine', label: 'GRN heat line (Upload MTC)' },
  { value: 'OpeningStockBatchLine', label: 'Opening stock line (Upload MTC)' },
];

function getFileIcon(fileType?: string, mimeType?: string) {
  const type = (fileType || mimeType || '').toLowerCase();
  if (type.includes('pdf')) return <FilePdfOutlined style={{ color: '#ff4d4f' }} />;
  if (type.includes('image')) return <FileImageOutlined style={{ color: '#52c41a' }} />;
  return <FileOutlined />;
}

const CompanyDocumentLibraryPage: React.FC = () => {
  const { user } = useAuthStore();
  const [searchParams] = useSearchParams();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [entityType, setEntityType] = useState<string>('');
  const [documentTypeInput, setDocumentTypeInput] = useState('');
  const [documentType, setDocumentType] = useState('');

  useEffect(() => {
    const doc = searchParams.get('doc')?.trim();
    if (doc) {
      setSearchInput(doc);
    }
  }, [searchParams]);

  useEffect(() => {
    const t = window.setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 300);
    return () => window.clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    const t = window.setTimeout(() => {
      setDocumentType(documentTypeInput.trim());
      setPage(1);
    }, 300);
    return () => window.clearTimeout(t);
  }, [documentTypeInput]);

  const listFilters = useMemo(
    () => ({
      search: search || undefined,
      entity_type: entityType || undefined,
      document_type: documentType || undefined,
    }),
    [search, entityType, documentType]
  );

  const { data, isLoading, isFetching, refetch } = useDocumentsPaged(listFilters, page, pageSize);
  const deleteMutation = useDeleteDocument();
  const items = data?.items ?? [];
  const total = data?.total ?? 0;

  const copyText = async (label: string, value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      message.success(`${label} copied`);
    } catch {
      message.error('Could not copy');
    }
  };

  const handleDownload = async (doc: Document) => {
    try {
      const apiUrl = API_BASE_URL;
      const token = localStorage.getItem('token');
      const response = await fetch(`${apiUrl}/documents/${doc.id}/download`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error(String(response.status));
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = doc.file_name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      message.success('Download started');
      refetch();
    } catch {
      message.error('Failed to download document');
    }
  };

  const handleView = async (doc: Document) => {
    try {
      const apiUrl = API_BASE_URL;
      const token = localStorage.getItem('token');
      const viewUrl = `${apiUrl}/documents/${doc.id}/view`;
      const response = await fetch(viewUrl, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error(String(response.status));
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    } catch {
      message.error('Failed to open document');
    }
  };

  const columns: ColumnsType<Document> = [
    {
      title: 'Document #',
      dataIndex: 'document_number',
      key: 'document_number',
      width: 150,
      render: (num: string, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>{num}</Text>
          <Button
            type="link"
            size="small"
            icon={<CopyOutlined />}
            onClick={() => copyText('Document number', record.document_number)}
          >
            Copy number
          </Button>
        </Space>
      ),
    },
    {
      title: 'Name / file',
      key: 'name',
      render: (_, record) => (
        <Space>
          {getFileIcon(record.file_type, record.mime_type)}
          <div>
            <div style={{ fontWeight: 500 }}>{record.document_name}</div>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {record.file_name} · {documentService.formatFileSize(record.file_size_bytes)}
            </Text>
          </div>
        </Space>
      ),
    },
    {
      title: 'Entity',
      key: 'entity',
      width: 200,
      render: (_, record) => (
        <span>
          {record.entity_type ? <Tag>{record.entity_type}</Tag> : <Text type="secondary">—</Text>}
          {record.entity_id && (
            <div style={{ marginTop: 4, fontSize: 11, wordBreak: 'break-all' }}>{record.entity_id}</div>
          )}
        </span>
      ),
    },
    {
      title: 'Type',
      dataIndex: 'document_type',
      key: 'document_type',
      width: 120,
      render: (t) => (t ? <Tag>{t}</Tag> : '—'),
    },
    {
      title: 'Uploaded',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 120,
      render: (d: string) => dayjs(d).format('DD/MM/YYYY HH:mm'),
    },
    {
      title: 'GRN / MTC',
      key: 'mtc',
      width: 130,
      render: (_, record) => (
        <Button
          type="link"
          size="small"
          icon={<CopyOutlined />}
          onClick={() => copyText('Document UUID', record.id)}
        >
          Copy UUID
        </Button>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 140,
      fixed: 'right',
      render: (_, record) => (
        <Space>
          <Tooltip title="View">
            <Button type="link" icon={<EyeOutlined />} onClick={() => handleView(record)} />
          </Tooltip>
          <Tooltip title="Download">
            <Button type="link" icon={<DownloadOutlined />} onClick={() => handleDownload(record)} />
          </Tooltip>
          {user?.id === record.uploaded_by && (
            <Popconfirm
              title="Delete this document?"
              onConfirm={async () => {
                try {
                  await deleteMutation.mutateAsync(record.id);
                  refetch();
                } catch {
                  /* mutation shows error */
                }
              }}
            >
              <Tooltip title="Delete">
                <Button type="link" danger icon={<DeleteOutlined />} />
              </Tooltip>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Title level={4}>Document library</Title>
      <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
        All documents for your company. Use <strong>Upload MTC</strong> on a draft GRN heat line so certificates link to{' '}
        <Tag>GrnItemHeatLine</Tag> — then find them here and copy the <strong>UUID</strong> or <strong>document number</strong> into
        the heat line field if needed. General files can be uploaded here without linking to a record.
      </Text>

      <Card style={{ marginBottom: 16 }}>
        <Space wrap align="start">
          <DocumentUpload buttonText="Upload to library" />
          <Input.Search
            allowClear
            placeholder="Search name, number, description…"
            style={{ width: 280 }}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onSearch={(v) => {
              setSearchInput(v);
              setSearch(v.trim());
              setPage(1);
            }}
          />
          <Select
            style={{ width: 240 }}
            value={entityType}
            onChange={(v) => {
              setEntityType(v);
              setPage(1);
            }}
            options={ENTITY_FILTER_OPTIONS}
          />
          <Input
            placeholder="Document type (e.g. Certificate)"
            style={{ width: 200 }}
            value={documentTypeInput}
            onChange={(e) => setDocumentTypeInput(e.target.value)}
            allowClear
          />
          <Button onClick={() => refetch()} loading={isFetching}>
            Refresh
          </Button>
        </Space>
      </Card>

      <Card>
        <Table<Document>
          rowKey="id"
          loading={isLoading || isFetching}
          columns={columns}
          dataSource={items}
          scroll={{ x: 1100 }}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            pageSizeOptions: ['10', '25', '50', '100'],
            showTotal: (t) => `${t} documents`,
            onChange: (p, ps) => {
              setPage(p);
              setPageSize(ps);
            },
          }}
        />
      </Card>
    </div>
  );
};

export default CompanyDocumentLibraryPage;
